import { createApi } from '@reduxjs/toolkit/query/react';
import {
  CreateOrganizerEventRequestBody,
  GetEventCloneSummaryResponse,
  GetEventCheckListByEventIdResponse,
  GetEventSetupTrackResponse,
  GetReusableTracksResponse,
  GetOrganizerContactsInvitesByEventIdResponse,
  GetOrganizerEventByIdResponse,
  GetOrganizerEventsFilterResponse,
  GetOrganizerEventsResponse,
  GetOrganizerEventsSourcesFilterResponse,
  GetOrganizerUserInvitesByEventIdResponse,
  PatchOrganizerEventByIdResponse,
  PatchOrganizerEventStatusByIdResponse,
  GetOrganizerEventProductsResponse,
  GetOrganizerEventCreativeIdeasResponse,
  OrganizerEventCreativeIdea,
  PatchOrganizerEventCreativeIdeaRequest,
  GetOrganizerTlaPostsResponse,
  GetReusableEventSubdomainsResponse,
  PatchOrganizerEventSubdomainResponse,
  PutOrganizerEventProductRequest,
  PutOrganizerEventProductResponse,
  PostCreateOrganizerEventInvitationsRequestBody,
  PostCreateOrganizerEventResponse,
  Subdomain,
} from './models';
import { useApiReq } from 'lib.hooks/src';
import type { GetCreativesDeskArtifactResponse } from 'lib.store/src/type-partner/creatives-desk/models';
import { LeadsRequest } from 'lib.store/src/models-to-replace/leads';
import { FiltersParams } from 'lib.store/src/models';
import { getQueryStringFromParams } from 'lib.shared/src/utils';
import { ContactsFilters } from './models';
import { API_EVENT_TAGS } from 'lib.store/src/tags';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import { apiBaseUrl } from 'lib.shared/src/api';

export const organizerEvents = createApi({
  reducerPath: API_EVENT_TAGS.ORGANIZER_EVENTS,
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    API_EVENT_TAGS.ORGANIZER_EVENTS,
    API_EVENT_TAGS.ORGANIZER_EVENTS_INVITATIONS,
    API_EVENT_TAGS.EVENTS_SETUP_PROGRESS,
    API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID,
    API_EVENT_TAGS.EVENTS_SETUP_TRACK,
  ],
  endpoints: (build) => ({
    getOrganizerEvents: build.query<
      GetOrganizerEventsResponse,
      {
        filters?: {
          search: string;
          date?: {
            from: string;
            to: string;
          };
          statuses?: string[];
          page?: number;
        } | null;
        page?: number;
      }
    >({
      query: ({ filters, page } = {}) => {
        const params = new URLSearchParams();

        if (Array.isArray(filters?.statuses)) {
          filters.statuses.forEach((id) => {
            params.append('filters[statuses][]', id.toString());
          });
        }

        if (filters?.search) {
          params.append('filters[search]', filters.search);
        }

        const pageParam = filters?.page ?? page;

        if (pageParam) {
          params.append('page', pageParam.toString());
        }

        if (filters?.date?.from) {
          params.append('filters[date][from]', `${filters.date.from}`);
        }

        if (filters?.date?.to) {
          params.append('filters[date][to]', `${filters.date.to}`);
        }

        const queryString = params.toString();

        return {
          url: `organizer/events${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: () => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
      ],
    }),

    postCreateOrganizerEvent: build.mutation<
      PostCreateOrganizerEventResponse,
      CreateOrganizerEventRequestBody
    >({
      query: (body) => {
        return {
          body,
          method: 'POST',
          url: '/organizer/events',
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      invalidatesTags: () => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
      ],
    }),
    getOrganizerEventById: build.query<GetOrganizerEventByIdResponse, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
      ],
    }),
    getEventCloneSummary: build.query<GetEventCloneSummaryResponse, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/clone-summary`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: `clone-summary-${eventId}` },
      ],
    }),
    getEventCheckListByEventId: build.query<
      GetEventCheckListByEventIdResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/setup/checklist`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS],
    }),
    getEventSetupTrack: build.query<GetEventSetupTrackResponse, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/setup/track`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.EVENTS_SETUP_TRACK, id: eventId },
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
      ],
    }),

    getReusableTracks: build.query<GetReusableTracksResponse, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/setup/track/reusable`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.EVENTS_SETUP_TRACK, id: `reusable-${eventId}` },
      ],
    }),

    reuseTrack: build.mutation<GetEventSetupTrackResponse, { eventId: string; trackId: number }>({
      query: ({ eventId, trackId }) => ({
        url: `/organizer/events/${eventId}/setup/track/reuse/${trackId}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.EVENTS_SETUP_TRACK, id: `reusable-${eventId}` },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
      ],
      async onQueryStarted({ eventId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            organizerEvents.util.updateQueryData('getEventSetupTrack', { eventId }, () => data),
          );
        } catch {
          /* mutation failed */
        }
      },
    }),

    postEventSetupTrackToEmail: build.mutation<
      GetEventSetupTrackResponse,
      { eventId: string; email: string; snippet: string }
    >({
      query: ({ eventId, email, snippet }) => ({
        url: `/organizer/events/${eventId}/setup/track/send-manual`,
        headers: {
          Accept: 'application/json',
        },
        method: 'POST',
        body: {
          email,
          snippet,
        },
        providesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS],
      }),
    }),
    patchOrganizerEventById: build.mutation<
      PatchOrganizerEventByIdResponse,
      { eventId: string; event: CreateOrganizerEventRequestBody }
    >({
      query: ({ event, eventId }) => ({
        url: `/organizer/events/${eventId}`,
        method: 'PATCH',
        body: event,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
      ],
    }),

    patchOrganizerEventStatusById: build.mutation<
      PatchOrganizerEventStatusByIdResponse,
      { eventId: string; status: string; transition: number }
    >({
      query: ({ eventId, status, transition }) => ({
        url: `/organizer/events/${eventId}/status/${Number(status)}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: { transition },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
      ],
    }),

    // INVITATIONS
    getOrganizerUserInvitesByEventId: build.query<
      GetOrganizerUserInvitesByEventIdResponse,
      {
        eventId: string;
        page?: number;
        search?: string;
        filters: { tags: string[]; statuses: string[] };
      }
    >({
      query: ({ eventId, page, filters }) => {
        const partnersQueryString = getQueryStringFromParams(filters?.statuses, 'statuses');
        const eventsQueryString = getQueryStringFromParams(filters?.tags, 'tags');
        const pageQueryString = page ? `page=${page}` : '';

        const queryString = [partnersQueryString, eventsQueryString, pageQueryString]
          .filter(Boolean)
          .join('&');

        return {
          url: `organizer/events/${eventId}/users-invites${queryString.length ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS_INVITATIONS],
    }),

    getOrganizerContactsInvitesByEventId: build.query<
      GetOrganizerContactsInvitesByEventIdResponse,
      { eventId: string; page?: number; filters: Partial<ContactsFilters> }
    >({
      query: ({ eventId, page, filters }) => {
        const statusesQueryString = getQueryStringFromParams(filters?.statuses, 'statuses');
        const companiesQueryString = getQueryStringFromParams(filters?.companies, 'companies');
        const sourcesQueryString = (filters?.sources || [])
          .flatMap((source, index) => [
            `filters[sources][${index}][id]=${encodeURIComponent(source.id)}`,
            `filters[sources][${index}][type]=${encodeURIComponent(source.type)}`,
          ])
          .join('&');
        const isInvitedQueryString = filters?.is_invited ? `is_invited=true` : '';
        const idsQueryString = getQueryStringFromParams(filters?.ids, 'ids');
        const searchQueryString = filters?.search ? `filters[search]=${filters.search}` : '';

        const pageQueryString = page ? `page=${page}` : '';

        const queryString = [
          statusesQueryString,
          companiesQueryString,
          sourcesQueryString,
          isInvitedQueryString,
          idsQueryString,
          pageQueryString,
          searchQueryString,
        ]
          .filter(Boolean)
          .join('&');

        return {
          url: `organizer/events/${eventId}/users-invites${queryString.length ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS_INVITATIONS],
    }),
    postCreateOrganizerContactInvitation: build.mutation<any, { eventId: string; id: string[] }>({
      query: ({ eventId, id }) => {
        return {
          method: 'POST',
          body: { id },
          url: `organizer/events/${eventId}/users-invites`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS_INVITATIONS],
    }),
    deleteOrganizerContactInvitation: build.mutation<
      any,
      { eventId: string; filters: Partial<ContactsFilters> }
    >({
      query: ({ eventId, filters }) => {
        return {
          method: 'DELETE',
          body: { filters },
          url: `organizer/events/${eventId}/users-invites`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS_INVITATIONS],
    }),

    postCreateOrganizerEventInvitations: build.mutation<
      void,
      { eventId: string; body: PostCreateOrganizerEventInvitationsRequestBody }
    >({
      query: ({ eventId, body }) => {
        return {
          body,
          method: 'POST',
          url: `organizer/events/${eventId}/users-invites`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      invalidatesTags: [
        API_EVENT_TAGS.ORGANIZER_EVENTS,
        API_EVENT_TAGS.ORGANIZER_EVENTS_INVITATIONS,
      ],
    }),

    postSendOrganizerEventInvitations: build.mutation<
      void,
      { eventId: string; filters: Partial<ContactsFilters> }
    >({
      query: ({ eventId, filters }) => {
        return {
          body: { filters },
          method: 'POST',
          url: `organizer/events/${eventId}/users-invites/sent`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      invalidatesTags: [
        API_EVENT_TAGS.ORGANIZER_EVENTS,
        API_EVENT_TAGS.ORGANIZER_EVENTS_INVITATIONS,
      ],
    }),

    patchOrganizerEventInvitationTag: build.mutation<
      void,
      { tag: string; eventId: string; ids: string[] }
    >({
      query: ({ tag, ids, eventId }) => {
        return {
          body: { tag, ids },
          method: 'PATCH',
          url: `/organizer/events/${eventId}/users-invites/tag`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS],
    }),

    //SUBDOMAIN
    getOrganizerEventSubdomainById: build.query<
      PatchOrganizerEventSubdomainResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/setup/subdomain`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS],
    }),
    patchOrganizerEventSubdomain: build.mutation<Subdomain, { subdomain: string; eventId: string }>(
      {
        query: ({ subdomain, eventId }) => ({
          url: `/organizer/events/${eventId}/setup/subdomain`,
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: {
            subdomain,
          },
        }),
        invalidatesTags: (_result, _error, { eventId }) => [
          { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
          { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
          { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
        ],
      },
    ),
    getReusableEventSubdomains: build.query<
      GetReusableEventSubdomainsResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/setup/subdomain/reusable`,
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      providesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS, id: `subdomain-reusable-${eventId}` },
      ],
    }),
    reuseEventSubdomain: build.mutation<Subdomain, { eventId: string; subdomainId: string }>({
      query: ({ eventId, subdomainId }) => ({
        url: `/organizer/events/${eventId}/setup/subdomain/reuse/${subdomainId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS, id: `subdomain-reusable-${eventId}` },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
        // the event gained a subdomain, so its cached clone summary is stale
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: `clone-summary-${eventId}` },
      ],
    }),

    //PRODUCTS (paid placements: TLA posts)
    getOrganizerEventCreativeIdeas: build.query<
      GetOrganizerEventCreativeIdeasResponse,
      { eventId: string }
    >({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/creative-ideas`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_r, _e, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS, id: `creative-ideas-${eventId}` },
      ],
    }),
    patchOrganizerEventCreativeIdea: build.mutation<
      { data: OrganizerEventCreativeIdea },
      PatchOrganizerEventCreativeIdeaRequest
    >({
      query: ({ eventId, ideaId, ...body }) => ({
        url: `/organizer/events/${eventId}/creative-ideas/${ideaId}`,
        method: 'PATCH',
        body,
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: (_r, _e, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS, id: `creative-ideas-${eventId}` },
      ],
    }),
    // one idea's preview, framed as a post of this event — the sponsors' picture
    getOrganizerEventCreativeIdeaArtifact: build.query<
      GetCreativesDeskArtifactResponse,
      { eventId: string; ideaId: number }
    >({
      query: ({ eventId, ideaId }) => ({
        url: `/organizer/events/${eventId}/creative-ideas/${ideaId}/artifact`,
        headers: {
          Accept: 'application/json',
        },
      }),
    }),
    getOrganizerEventProducts: build.query<GetOrganizerEventProductsResponse, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/products`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS],
    }),
    putOrganizerEventProduct: build.mutation<
      PutOrganizerEventProductResponse,
      PutOrganizerEventProductRequest
    >({
      query: ({ eventId, type, channel, price, monthly_limit, status }) => ({
        url: `/organizer/events/${eventId}/products/${type}`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: { channel, price, monthly_limit, status },
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS],
    }),
    getOrganizerEventTlaPosts: build.query<GetOrganizerTlaPostsResponse, { eventId: string }>({
      query: ({ eventId }) => ({
        url: `/organizer/events/${eventId}/tla-posts`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS],
    }),

    //FILTERS
    getOrganizerEventsFilterOptions: build.query<
      GetOrganizerEventsFilterResponse,
      {
        search?: string;
        page?: number;
        billings?: string[];
        filters?: FiltersParams;
      }
    >({
      query: ({ search, page, billings, filters }) => {
        const params = new URLSearchParams();

        if (billings?.length) {
          billings.forEach((billing) => {
            params.append('billings[]', billing);
          });
        }

        const relationsQuery = [
          getQueryStringFromParams(filters?.events, 'events', 'relations'),
          getQueryStringFromParams(filters?.landings, 'landings', 'relations'),
          getQueryStringFromParams(filters?.campaigns, 'campaigns', 'relations'),
          getQueryStringFromParams(filters?.partners, 'partners', 'relations'),
        ]
          .filter(Boolean)
          .join('&');

        if (relationsQuery) {
          new URLSearchParams(relationsQuery).forEach((value, key) => {
            params.append(key, value);
          });
        }

        if (search && search.length) {
          params.append('search', search);
        }

        const pageParam = page ?? filters?.page;

        if (typeof pageParam === 'number') {
          params.append('page', pageParam.toString());
        }

        const queryString = params.toString();

        return {
          url: `/organizer/events/filter${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json', // Specify JSON response
          },
        };
      },
      providesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS],
    }),

    getOrganizerEventsCompaniesFilter: build.query<
      GetOrganizerEventsFilterResponse,
      { search?: string; eventId: string; page?: number }
    >({
      query: ({ search, eventId }) => ({
        url: `organizer/events/${eventId}/users-invites/companies/filter${search ? `?search=${search}` : ''}`,
      }),
    }),

    getOrganizerEventsSourcesFilter: build.query<
      GetOrganizerEventsSourcesFilterResponse,
      { search?: string; eventId: string; page?: number }
    >({
      query: ({ search, eventId }) => ({
        url: `organizer/events/${eventId}/users-invites/sources/filter${search ? `?search=${search}` : ''}`,
      }),
    }),

    postApproveUploadContactsCsv: build.mutation<
      void,
      { eventId: string; body: { map: Record<string, string>; source_id: string } }
    >({
      query: ({ eventId, body }) => ({
        url: `organizer/events/${eventId}/users-invites/import`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [API_EVENT_TAGS.ORGANIZER_EVENTS_INVITATIONS],
    }),
    postSetupAudienceCatalog: build.mutation<void, { eventId: string; audienceCatalogId: string }>({
      query: ({ eventId, audienceCatalogId }) => ({
        url: `/organizer/events/${eventId}/setup/audience-catalog/${audienceCatalogId}`,
        method: 'POST',
        headers: {
          Accept: 'application/json', // Specify JSON response
        },
      }),
      invalidatesTags: (_result, _error, { eventId }) => [
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS },
        { type: API_EVENT_TAGS.EVENTS_SETUP_PROGRESS },
        { type: API_EVENT_TAGS.ORGANIZER_EVENTS_BY_ID, id: eventId },
      ],
    }),
  }),
});

export const useGetInvitationsExported = (eventId: string) => {
  return useApiReq<never, LeadsRequest>({
    reqConfig: {
      method: 'GET',
      url: `${apiBaseUrl}/organizer/events/${eventId}/users-invites/export`,
      headers: {
        'Content-Type': 'text/csv',
      },
    },
  });
};

export const {
  useGetOrganizerEventsQuery,
  useLazyGetOrganizerEventsQuery,
  usePostCreateOrganizerEventMutation,
  useGetOrganizerEventByIdQuery,
  useGetEventCloneSummaryQuery,
  usePatchOrganizerEventByIdMutation,
  useGetOrganizerEventsFilterOptionsQuery,
  usePatchOrganizerEventStatusByIdMutation,
  usePatchOrganizerEventInvitationTagMutation,
  useGetEventCheckListByEventIdQuery,
  useGetEventSetupTrackQuery,
  useGetReusableTracksQuery,
  useReuseTrackMutation,
  usePostEventSetupTrackToEmailMutation,

  useGetOrganizerUserInvitesByEventIdQuery,
  useGetOrganizerContactsInvitesByEventIdQuery,

  useGetOrganizerEventSubdomainByIdQuery,
  useGetOrganizerEventProductsQuery,
  useGetOrganizerEventCreativeIdeasQuery,
  usePatchOrganizerEventCreativeIdeaMutation,
  useGetOrganizerEventCreativeIdeaArtifactQuery,
  usePutOrganizerEventProductMutation,
  useGetOrganizerEventTlaPostsQuery,
  usePatchOrganizerEventSubdomainMutation,
  useGetReusableEventSubdomainsQuery,
  useReuseEventSubdomainMutation,
  usePostCreateOrganizerContactInvitationMutation,
  usePostCreateOrganizerEventInvitationsMutation,
  useDeleteOrganizerContactInvitationMutation,

  usePostSendOrganizerEventInvitationsMutation,
  useGetOrganizerEventsCompaniesFilterQuery,
  useGetOrganizerEventsSourcesFilterQuery,
  usePostApproveUploadContactsCsvMutation,

  usePostSetupAudienceCatalogMutation,
} = organizerEvents;
